<template>
  <Card>
    <CardHeader>
      <CardTitle>2. Transaction matching</CardTitle>
      <CardDescription>
        Test how a real bank transaction description enriches, then confirm whether this retailer is
        compatible.
      </CardDescription>
    </CardHeader>

    <CardContent class="space-y-5">
      <div class="grid gap-4 sm:grid-cols-3">
        <div class="space-y-1.5 sm:col-span-2">
          <Label>Transaction description</Label>
          <Input v-model="description" placeholder="e.g. ACME OUTDOORS LONDON GB" />
        </div>
        <div class="space-y-1.5">
          <Label>Amount</Label>
          <Input v-model.number="amount" type="number" placeholder="0.00" />
        </div>
      </div>

      <div>
        <Button variant="secondary" :disabled="!canTest || testing" @click="testEnrichment">
          <Loader2 v-if="testing" class="w-4 h-4 animate-spin" />
          <Sparkles v-else class="w-4 h-4" />
          Test enrichment
        </Button>
      </div>

      <!-- Result -->
      <div v-if="result" class="rounded-lg border border-border p-4 space-y-3">
        <div class="flex items-center gap-3">
          <img
            v-if="enriched && enriched.logo"
            :src="enriched.logo"
            class="object-contain w-10 h-10 border border-border bg-white rounded-lg"
          />
          <div>
            <p class="text-sm font-medium">
              {{ (enriched && enriched.merchant) || 'No merchant matched' }}
            </p>
            <p v-if="enriched && enriched.website" class="text-xs text-muted-foreground">
              {{ enriched.website }}
            </p>
          </div>
        </div>
        <div v-if="enriched && enriched.labels && enriched.labels.length" class="flex flex-wrap gap-1.5">
          <Badge v-for="label in enriched.labels" :key="label" variant="secondary">{{ label }}</Badge>
        </div>

        <div class="border-t border-border pt-3">
          <p class="text-sm font-medium mb-2">Is this enrichment correct for your retailer?</p>
          <div class="flex gap-2">
            <Button
              :variant="compatible === true ? 'default' : 'outline'"
              size="sm"
              @click="compatible = true"
            >
              <Check class="w-4 h-4" /> Yes, compatible
            </Button>
            <Button
              :variant="compatible === false ? 'destructive' : 'outline'"
              size="sm"
              @click="compatible = false"
            >
              <X class="w-4 h-4" /> Not compatible
            </Button>
          </div>
          <p v-if="compatible === false" class="text-xs text-muted-foreground mt-2">
            You can still continue — the offer will rely on the descriptions you provide later.
          </p>
        </div>
      </div>

      <p v-if="error" class="text-sm text-destructive">{{ error }}</p>
    </CardContent>

    <CardFooter class="justify-between gap-2">
      <Button variant="outline" @click="back">
        <MoveLeft class="w-4 h-4" /> Back
      </Button>
      <Button :disabled="compatible === null || saving" @click="next">
        <Loader2 v-if="saving" class="w-4 h-4 animate-spin" />
        Save retailer & continue
        <MoveRight v-if="!saving" class="w-4 h-4" />
      </Button>
    </CardFooter>
  </Card>
</template>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
  Input,
  Label,
  Badge,
} from '@boogi/ui'
import { Sparkles, Check, X, MoveRight, MoveLeft, Loader2 } from 'lucide-vue-next'
import { addOfferWizard } from '@/stores/addOfferWizard'
import { wizardApi } from './wizardApi'

export default {
  name: 'step-transaction',
  mixins: [wizardApi],
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
    Button,
    Input,
    Label,
    Badge,
    Sparkles,
    Check,
    X,
    MoveRight,
    MoveLeft,
    Loader2,
  },
  data() {
    return {
      wizard: addOfferWizard,
      description: '',
      amount: null,
      testing: false,
      saving: false,
      result: null,
      compatible: null,
      error: '',
    }
  },
  computed: {
    canTest() {
      return this.description.trim() !== '' && this.amount !== null && this.amount !== ''
    },
    enriched() {
      return this.result?.enrichedCreditorData
    },
  },
  created() {
    // Guard: this step is only relevant when creating a new retailer.
    if (this.wizard.selectedRetailer || !this.wizard.retailer.retailerName) {
      this.$router.replace('/add-offer/retailer')
      return
    }
    this.description = this.wizard.retailer.retailerName
  },
  methods: {
    async testEnrichment() {
      this.error = ''
      this.testing = true
      this.compatible = null
      try {
        this.result = await this.$api(this.byooPath('/test-enrichment'), {
          method: 'POST',
          body: { description: this.description.trim(), amount: Number(this.amount) },
        })
        // Default suggestion based on whether a merchant was matched.
        this.compatible = !!this.result?.enrichedCreditorData?.merchant
      } catch (e) {
        this.error = e.message
      } finally {
        this.testing = false
      }
    },
    back() {
      this.$router.push('/add-offer/retailer')
    },
    async next() {
      if (this.compatible === null) return
      this.error = ''
      this.saving = true
      try {
        const r = this.wizard.retailer
        r.obCompatiable = this.compatible
        this.wizard.enrichment = {
          description: this.description.trim(),
          enrichmentCompatiable: this.compatible,
        }

        // Create the retailer record.
        const created = await this.$api(this.byooPath('/retailer'), {
          method: 'POST',
          body: {
            retailerName: r.retailerName,
            retailerOpenBankingDescription: this.description.trim(),
            providedLogo: '',
            providedWebsite: r.retailerWebsite,
            obPublisherCompatible: this.compatible,
            monthlyWebVisits: r.monthlyWebVisits,
            goodForBusiness: r.goodForBusiness,
            luxury: r.luxury,
            subscription: r.subscription,
          },
        })

        const retailerId = created?.retailer?.id
        if (!retailerId) throw new Error('Retailer was created but no id was returned.')
        r.id = retailerId

        // Upload the logo file captured in step 1.
        if (r.retailerLogoFile) {
          const formData = new FormData()
          formData.append('multipartFile', r.retailerLogoFile)
          const uploaded = await this.$api(
            this.byooPath(`/retailer/upload-image/${retailerId}`),
            { method: 'POST', body: formData, isForm: true }
          )
          if (uploaded?.logo) r.retailerLogo = uploaded.logo
        }

        this.$router.push('/add-offer/mids')
      } catch (e) {
        this.error = e.message
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
